/*
  # Docket Suggestions Service

  Handles fetching docket number suggestions from the ECFS API
  for autocomplete functionality in the search bar.
*/

export interface DocketSuggestion {
  docketNumber: string;
  displayText: string;
}

export interface DocketSuggestionsResponse {
  suggestions: DocketSuggestion[];
  query: string;
  total: number;
  timestamp?: number;
}

class DocketSuggestionsService {
  private cache = new Map<string, DocketSuggestionsResponse>();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private pendingRequests = new Map<string, Promise<DocketSuggestionsResponse>>();

  /**
   * Fetch docket suggestions for a given query
   */
  async getSuggestions(query: string, limit: number = 10): Promise<DocketSuggestionsResponse> {
    if (!query || query.trim().length < 2) {
      return { suggestions: [], query: query.trim(), total: 0 };
    }

    const trimmedQuery = query.trim();
    const cacheKey = `${trimmedQuery}-${limit}`;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached;
    }

    // Check if there's already a pending request for this query
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Create new request
    const requestPromise = this.fetchSuggestionsFromAPI(trimmedQuery, limit);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result with timestamp
      this.cache.set(cacheKey, {
        ...result,
        timestamp: Date.now()
      } as DocketSuggestionsResponse & { timestamp: number });

      return result;
    } finally {
      // Remove from pending requests
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Fetch suggestions from the Supabase function
   */
  private async fetchSuggestionsFromAPI(query: string, limit: number): Promise<DocketSuggestionsResponse> {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-docket-suggestions`;
    
    const headers = {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, limit }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache size for debugging
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}

export const docketSuggestionsService = new DocketSuggestionsService();
